import { forkJoin as observableForkJoin, Subscription, Observable } from "rxjs";
import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  OnDestroy
} from "@angular/core";
import {
  Source,
  SourceQuestion,
  SourceService
} from "../../../../../swaggerServices";
import { SourceWithSourceQuestions } from "../../../../../swaggerServices/model/sourceWithSourceQuestions";
import { SourceQuestionService } from "../../../../../swaggerServices";
import { ListOption } from "../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { cloneDeep } from "lodash-es";
import { DropdownListOption } from "../../../../shared/components/search-dropdown/search-dropdown.component";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { TaxYearsService } from "../../../../shared/util/tax-years.service";
import { ActivatedRoute } from "@angular/router";
import { Tenant } from "../../../../shared/types/tenant.enum";
import { ConfigStateService } from "../../../config/config-state.service";
import { environment } from "../../../../environments/environment";
import { TenantConfigService } from "../../../../shared/services/tenant-config.service";
import { TranslocoService } from "@jsverse/transloco";

declare var $: any;

interface ISourceQuestion extends SourceQuestion {
  id?: number;
  year?: number;
  sourceId?: number;
  question?: string;
  dataType?: string;
  isTable?: boolean;
  isTableColumn?: boolean;
  inGroup?: boolean;
  parentId?: number;
  parentQuestion?: string;
  mandatory?: boolean;
  spouse?: boolean;
  sourceFormat?: string;
  config?: string;
  edit?: boolean;
  externalIdentifier?: string;
  status?: number;
}

interface ISource extends SourceWithSourceQuestions {
  sourceQuestions?: ISourceQuestion[];
  edit?: boolean;
}

@Component({
    selector: "app-source",
    templateUrl: "./source.component.html",
    styleUrls: ["./source.component.scss"],
    standalone: false
})
export class SourceComponent implements OnInit, OnDestroy {
  routeSub: Subscription;
  source: ISource;
  originalSource: ISource;
  clickedRow: boolean;
  isSaving: boolean;
  @ViewChild("yearDropDown") yearDropDown: ElementRef;
  searchForm: UntypedFormGroup;
  taxYear: UntypedFormControl;
  taxYearOptions: DropdownListOption[];
  loading?: boolean;
  showConfig = false;
  isProductionMode: boolean;
  uiConfig: TenantConfigService;
  yearLabel: string;
  subscriptions: Subscription[] = [];
  sourceQuestionsWithWarning: string;

  @ViewChild("sourceTemplateUpload") sourceTemplateUpload: ElementRef;
  @ViewChild("sourceQuestionUploadWarningsModal")
  sourceQuestionUploadWarningsModal: ElementRef;
  errorMessage: string = "";
  isLoadingTemplate: boolean = false;
  lang: string;
  oneTeamIntegrationEnabled: boolean;

  constructor(
    private sourceService: SourceService,
    private sourceQuestionService: SourceQuestionService,
    private taxYearsService: TaxYearsService,
    private activatedRoute: ActivatedRoute,
    private config: ConfigStateService,
    private translocoService: TranslocoService
  ) {
    this.isProductionMode = environment.production;
    translocoService.langChanges$.subscribe(l => (this.lang = l));
  }

  ngOnInit() {
    this.routeSub = this.taxYearsService
      .getTaxYears()
      .subscribe((years: any) => {
        this.taxYearOptions = years;
        const taxYr =
          this.activatedRoute.snapshot.queryParams.taxYear ||
          this.taxYearsService.getCurrentTaxYear();
        this.taxYear = new UntypedFormControl(taxYr);
        this.searchForm = new UntypedFormGroup({});
        this.searchForm.addControl("taxYear", this.taxYear);
        this.taxYear.valueChanges.subscribe(() =>
          this.getSource(this.taxYear.value.value)
        );
        this.showConfig =
          this.config.featureFlags.allowPartialReplace ||
          this.config.featureFlags.allowTaxReturnGeneratedId;
        this.oneTeamIntegrationEnabled = this.config.featureFlags.enableOneteamIntegration;
      });
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.translocoService
          .selectTranslate("year")
          .subscribe(value => (this.yearLabel = value));
      })
    );
  }

  getSource(year) {
    const srcId = this.activatedRoute.snapshot.params.id;
    this.sourceService
      .getSourceByIdWithSourceQuestions(srcId, year)
      .subscribe((source: SourceWithSourceQuestions) => {
        this.initSource(source);
      });
  }

  initSource(source: ISource) {
    this.source = source;
    this.originalSource = cloneDeep(source);
    this.initParentQuestions();
    this.initUI();
  }

  initUI() {
    setTimeout(() => {
      $(".ui.checkbox").checkbox();
      $("body").click(() =>
        !this.clickedRow ? this.unfocusValueRows() : undefined
      );
      $("#list").floatThead({
        zIndex: 9,
        scrollContainer: () => $("#table-cont")
      });
    });
  }

  // append parent question name
  initParentQuestions() {
    this.source.sourceQuestions.forEach(q => {
      if (q.parentId) {
        const parentQ = this.source.sourceQuestions.find(
          _q => _q.id === q.parentId
        );
        q.parentQuestion = parentQ.question;
      }
    });
  }

  addNewQuestion() {
    const question = {
      id: null,
      question: null,
      year: Number(this.taxYear.value.value),
      dataType: "string",
      isTable: false,
      isTableColumn: this.source.type === "csv", // default to checked for csv src
      inGroup: false,
      parentId: null,
      mandatory: false,
      spouse: false,
      sourceFormat: null,
      status: 0,
      includeInWizard: false,
      alternativeNames: "",
      templateHeaderRequired: false,
      sumToZero: false,
      entityMatch: false
    };
    this.source.sourceQuestions.unshift(question);
    // init ui controls
    setTimeout(() => {
      this.source.sourceQuestions[0].edit = true;
      $("table tr.active input:first-of-type").focus();
      $(".ui.checkbox").checkbox();
      this.initDataTypeDropdown(question);
      this.initParentQuestionDropdown(question);
    });
  }

  initDataTypeDropdown(question) {
    const valuesOptions = [
      {
        name: "String",
        value: "string",
        selected: question.dataType === "string"
      },
      {
        name: "Integer",
        value: "integer",
        selected: question.dataType === "integer"
      },
      {
        name: "Float",
        value: "float",
        selected: question.dataType === "float"
      },
      {
        name: "Boolean",
        value: "boolean",
        selected: question.dataType === "boolean"
      },
      {
        name: "Date",
        value: "date",
        selected: question.dataType === "date"
      },
      {
        name: "Image",
        value: "image",
        selected: question.dataType === "image"
      },
      {
        name: "File",
        value: "file",
        selected: question.dataType === "file"
      }
    ];

    setTimeout(() => {
      $("table tr.active .datatype.dropdown").dropdown({
        values: valuesOptions,
        placeholder: "Select...",
        fullTextSearch: "exact",
        preserveHTML: false,
        onChange: (dataType: string) => {
          if (dataType) question.dataType = dataType;
        }
      });
    });
  }

  initParentQuestionDropdown(question) {
    const valuesOptions = this.source.sourceQuestions
      .filter(q => q.id && q.id !== question.id && q.isTable && q.status === 0)
      .map(q => {
        return {
          name: q.question,
          value: q.id,
          selected: q.id === question.parentId
        };
      });

    setTimeout(() => {
      $("table tr.active .parent-question.dropdown").dropdown({
        values: valuesOptions,
        placeholder: "Select...",
        fullTextSearch: "exact",
        preserveHTML: false,
        onChange: (parentId: string) => {
          if (parentId) {
            question.parentId = Number(parentId);
            this.initParentQuestions();
          }
        }
      });
    });
  }

  editValue(question: ListOption, event) {
    this.errorMessage = null;
    this.unfocusValueRows();
    question.edit = true;
    $(event.target)
      .find("input")
      .focus();
    this.clickedRow = true; // prevent unfocusValueRows() causing row to de-activate
    setTimeout(() => (this.clickedRow = false));
    this.initDataTypeDropdown(question);
    this.initParentQuestionDropdown(question);
  }

  unfocusValueRows() {
    this.source && this.source.sourceQuestions.forEach(q => (q.edit = false));
  }

  deleteQuestion(question: ISourceQuestion, index: number) {
    if (confirm("Are you sure you want to remove this question?"))
      if (!question.id) this.source.sourceQuestions.splice(index, 1);
      else question.status = 99;
  }

  save() {
    this.errorMessage = null;
    this.isSaving = true;
    const saveObs = [];
    // non-saved question table
    const tableQuestion = this.source.sourceQuestions.find(
      sq => sq.isTable && sq.status === 0 && !sq.id
    );
    if (this.source.type === "csv" && tableQuestion) {
      const req = this.getSourceQuestionPayload(tableQuestion, this.source.id);
      this.sourceQuestionService
        .addSourceQuestion(req)
        .subscribe((response: any) => {
          const foundTableQ = this.source.sourceQuestions.find(
            _q => _q.isTable
          );
          foundTableQ.id = response.id;
          this.addNonTableQuestions();
        });
    } else {
      this.addNonTableQuestions();
    }
  }

  addNonTableQuestions() {
    this.errorMessage = null;
    const saveObs = [];
    this.source.sourceQuestions.forEach((q, index) => {
      if (this.source.type === "csv") {
        const tableQ = this.source.sourceQuestions.find(
          _q => _q.isTable && _q.status === 0
        );
        if (tableQ && q.isTableColumn && !q.isTable) {
          q.parentId = tableQ.id;
        }

        // set all other questions as column
        this.source.sourceQuestions.forEach(
          (sourceQuestion: ISourceQuestion, SqIndex) => {
            if (!sourceQuestion.isTable) {
              sourceQuestion.isTableColumn = true;
            }
            if (
              index !== SqIndex &&
              sourceQuestion.question === q.question &&
              sourceQuestion.status === 0 &&
              q.status === 0
            ) {
              this.translocoService
                .selectTranslate(
                  "This-question-already-exists-Change-question-name-manually-before-continuing",
                  { question: q.question }
                )
                .subscribe(value => (this.errorMessage = value));
            }
          }
        );

        q.externalIdentifier = q.question;
        if (!q.isTableColumn) {
          q.parentId = null;
        }
      } else {
        if (q.isTableColumn && !q.parentQuestion) {
          this.translocoService
            .selectTranslate("Parent-question-can-not-be-null")
            .subscribe(value => (this.errorMessage = value));
        }
      }
      if (!this.errorMessage) {
        const req = this.getSourceQuestionPayload(q, this.source.id);
        if (!q.id) {
          saveObs.push(this.sourceQuestionService.addSourceQuestion(req));
        } else {
          const originalQ = this.originalSource.sourceQuestions.find(
            _q => _q.id === q.id
          );
          if (originalQ) {
            let qHaschanged;
            const keysToCompare = [
              "externalIdentifier",
              "question",
              "dataType",
              "isTable",
              "isTableColumn",
              "inGroup",
              "parentId",
              "mandatory",
              "spouse",
              "sourceFormat",
              "config",
              "status",
              "includeInWizard",
              "alternativeNames",
              "templateHeaderRequired",
              "sumToZero",
              "entityMatch"
            ];
            keysToCompare.forEach(k => {
              if (q[k] !== originalQ[k]) qHaschanged = true;
            });
            if (qHaschanged) {
              saveObs.push(
                this.sourceQuestionService.updateSourceQuestion(req)
              );
            }
          }
        }
      }
    });

    if (saveObs.length > 0 && !this.errorMessage) {
      if (this.source.type !== "oneteam") {
        this.saveSourceQuestions(saveObs);
      } else {
        // save sequentially as Oneteam source allows to create questions with same name and unique tag
        let length = 0;
        const sub = () => {
          saveObs[length].subscribe(
            () => {
              length = length + 1;
              if (length < saveObs.length) sub();
              else this.getSourceQuestions();
            },
            () => (this.isSaving = false)
          );
        };
        sub();
      }
    } else {
      this.isSaving = false;
    }
  }

  getSourceQuestionPayload(q, sourceId) {
    return {
      id: q.id || null,
      year: q.year,
      sourceId: sourceId,
      externalIdentifier: q.externalIdentifier,
      question: q.question,
      dataType: q.dataType || "String",
      isTable: q.isTable,
      isTableColumn: q.isTableColumn,
      inGroup: q.inGroup,
      parentId: q.parentId,
      mandatory: q.mandatory,
      spouse: q.spouse,
      sourceFormat: q.sourceFormat,
      config: q.config,
      status: q.status,
      questionId: q.questionId,
      includeInWizard: q.includeInWizard,
      alternativeNames: q.alternativeNames,
      templateHeaderRequired: q.templateHeaderRequired,
      sumToZero: q.sumToZero,
      entityMatch: q.entityMatch
    };
  }

  saveSourceQuestions(saveObs: any) {
    observableForkJoin(saveObs).subscribe(
      () => {
        this.getSourceQuestions();
      },
      () => (this.isSaving = false)
    );
  }

  getSourceQuestions() {
    this.sourceService
      .getSourceByIdWithSourceQuestions(
        this.source.id,
        this.taxYear.value.value
      )
      .subscribe(
        (source: SourceWithSourceQuestions) => {
          this.initSource(source);
          this.isSaving = false;
        },
        () => (this.isSaving = false)
      );
  }

  // csv source can only have 1 table question
  canSelectTable(question: ISourceQuestion) {
    if (this.tableColumnIsDisabled(question)) {
      alert("Only 1 question can be set as table");
    } else {
      if (!question.isTable) {
        question.dataType = "table";
        question.isTableColumn = false;
        question.parentId = null;
      } else {
        question.dataType = "String";
      }
    }
  }

  tableColumnIsDisabled(question: ISourceQuestion) {
    return (
      this.source.type === "csv" &&
      !question.isTable &&
      this.source.sourceQuestions.some(q => q.isTable && q.status !== 99)
    );
  }

  reActivateQuestion(question: ISourceQuestion) {
    if (
      question.isTable &&
      this.source.type === "csv" &&
      this.source.sourceQuestions.some(q => q.isTable && q.status === 0)
    ) {
      alert(
        "Only 1 question can be set as table. You must de-select existing table question before re-activating this question"
      );
    } else {
      question.status = 0;
    }
  }

  // enforce 1 table question
  hasTableQ() {
    return (
      this.source.type === "csv" &&
      this.source.sourceQuestions.some(q => q.isTable && q.status === 0)
    );
  }

  ngOnDestroy() {
    this.routeSub.unsubscribe();
  }

  initFile(files: any) {
    this.isLoadingTemplate = true;

    const clean = (s: string) => {
      return (s || "").replace(/[\s]/g, "").toLocaleLowerCase();
    };

    this.sourceQuestionService.bulkAddsourceQuestions(files[0]).subscribe(
      (response: any) => {
        const headers = response.headers;
        let sqHasSameNameWithDeletedQuestions = [];
        headers.forEach(h => {
          //add header if it does not exist
          if (
            !this.source.sourceQuestions.some(
              q => clean(q.question) === clean(h) && q.status === 0
            )
          ) {
            const question = {
              id: null,
              question: h,
              year: Number(this.taxYear.value.value),
              dataType: "string",
              isTable: false,
              isTableColumn: true,
              inGroup: false,
              parentId: null,
              mandatory: false,
              spouse: false,
              sourceFormat: null,
              status: 0
            };
            this.source.sourceQuestions.push(question);
          }

          //noted source questions has the same name as previously deleted questions, if so, notify user before saving
          if (
            this.source.sourceQuestions.some(
              q => clean(q.question) === clean(h) && q.status === 99 && q.id
            )
          ) {
            sqHasSameNameWithDeletedQuestions.push(h);
          }
        });
        //show warning modal
        if (sqHasSameNameWithDeletedQuestions.length > 0) {
          this.sourceQuestionsWithWarning = sqHasSameNameWithDeletedQuestions.join(
            ", "
          );
          $(this.sourceQuestionUploadWarningsModal.nativeElement).modal("show");
        }

        this.isLoadingTemplate = false;
        this.sourceTemplateUpload.nativeElement.value = "";
      },
      err => {
        this.isLoadingTemplate = false;
        this.sourceTemplateUpload.nativeElement.value = "";
      }
    );
  }
}
